import AuthenticatedImage from '../AuthenticatedImage';
import CalendarGallery from 'src/components/Gallery/CalendarGallery';
import Clamp from 'react-multiline-clamp';
import config from 'src/constants/config';
import DateFormat from 'src/utils/dateFormat';
import htmlParse from 'html-react-parser';
import IconButton from 'src/components/Buttons/IconButton';
import moment from 'src/utils/moment';
import React, { useCallback, useRef } from 'react';
import SVG from '../../Images/SvgRenderer';
import theme from '../../../ui/theme';
import Tooltip from 'src/components/Layouts/Tooltip';
import useStyles from './style';
import { convertToKeyValueString, convertToSnakeCase, getSchoolSettings, isKey, removeDuplicatesJSON } from 'src/utils/useFunctions';
import { createNotification } from 'src/utils/createNotification';
import { getAppEnvironmentData } from 'src/utils/useApp';
import { getElementID } from 'src/utils/useUUID';
import { Popover } from '@mui/material';
import { saveAs } from 'file-saver';
import { setCalendarEventHandleModal, setChildCardModal, setConfirmModal, setConfirmOptionsModal, setEmployeeModal, setExcuseNotesDetailModal, setPostDetailModal, setSubstitutionsDetailModal } from 'src/store/actions/modals.actions';
import { deleteCalendarEvent, deleteCalendarRecurringEvents, deleteCalendarRecurringEventsSince, setCalendarHoverEvent } from 'src/store/actions/calendar.actions';
import { useAppDispatch, useAppSelector } from 'src/hooks/redux-hooks';
import { useMemo } from 'src/utils/useMemo';
import { useStates } from 'src/utils/useState';
import { useTranslation } from 'react-i18next';
import { weekdays } from 'src/constants/weekdays';

type CalendarEventItemType = {
  data: any;
  date: any;
  overlaping: boolean;
};

const CalendarEventItem: React.FunctionComponent<CalendarEventItemType> = ({ data, date = moment(), overlaping = true }) => {

  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const calendarData = useAppSelector((state: any) => state.calendar);
  const configurationData = useAppSelector((state: any) => state.configuration);
  const dataData = useAppSelector((state: any) => state.data);
  const languageData = useAppSelector((state: any) => state.language);
  const userData = useAppSelector((state: any) => state.user);
  const calendarService = useAppSelector((state: any) => state.services).calendarService;

  const calendarEventTypes = useMemo(() => configurationData.configuration.calendarEventTypes, [configurationData.configuration.calendarEventTypes]);

  const schoolSettings = userData.schoolSettings;

  const isShareable = true;

  const imgRef = useRef(null);

  const defaultChildren = dataData.children;

  const allEvents = calendarData.events;
  const hoverEvent = calendarData.hoverEvent;
  const eventID = data.eventID;
  const recurringEventID = data.recurringEventID;
  const recurringEventsCount = recurringEventID ? allEvents.filter((item: any) => item.recurringEventID === recurringEventID).length : 0;
  const eventPopover = getElementID();

  const eventOnlyClasses = Array.isArray(data.classToChild) ? removeDuplicatesJSON(data.classToChild.filter((item: any) => !item.childID).map((item: any) => { return { classID: item.classID }; })) : [];
  const eventChildren = Array.isArray(data.classToChild) ? removeDuplicatesJSON(data.classToChild.filter((item: any) => item.childID).map((item: any) => { return { childID: item.childID }; })) : [];
  const allChildren = Array.isArray(data.classToChild) ? removeDuplicatesJSON([eventOnlyClasses.map((item: any) => { return defaultChildren.filter((dataItem: any) => dataItem.classID.indexOf(item.classID) !== -1).map((dataItem: any) => { return { classID: item.classID, childID: dataItem.childID }; }); }).flat(), eventChildren].flat().map((item: any) => { return { classID: item.classID, childID: item.childID }; })) : [];

  const getUserData = useCallback((userID: any) => {
    return dataData.users.filter((item: any) => item.userID === userID).length === 0 ? dataData.users.find((item: any) => item.userID === -1) : dataData.users.find((item: any) => item.userID === userID);
  }, [dataData.users]);

  const [state, setState] = useStates({
    anchorEl: null,
    isVisible: false,
  });

  const handleClick = (e: any) => {
    setState("anchorEl", e.currentTarget);
    setState("isVisible", true);
  };

  const handleClose = () => {
    setState("anchorEl", null);
    setTimeout(() => {
      setState("isVisible", false);
    }, theme.transitions.duration.complex);
  };

  const getClassData = useCallback((classID: any) => {
    return dataData.classes.filter((item: any) => item.classID === classID).length === 0 ? [] : dataData.classes.find((item: any) => item.classID === classID);
  }, [dataData.classes]);

  const getChildData = useCallback((childID: any) => {
    return dataData.children.filter((item: any) => item.childID === childID).length === 0 ? [] : dataData.children.find((item: any) => item.childID === childID);
  }, [dataData.children]);

  const getEmployeeData = useCallback((employeeID: any) => {
    return dataData.employees.filter((item: any) => item.employeeID === employeeID).length === 0 ? [] : dataData.employees.find((item: any) => item.employeeID === employeeID);
  }, [dataData.employees]);

  const getIcon = useMemo(() => {
    if(calendarEventTypes.filter((item: any) => item.type === data.type).length !== 0) {
      const itemData = calendarEventTypes.find((item: any) => item.type === data.type);
      return (<img src={itemData.image} alt={itemData.type} ref={imgRef}/>);
    } else {
      return (<SVG src="question-mark-circle"/>);
    }
  }, [calendarEventTypes, data.type]);

  const getIconForCSS = useMemo(() => {
    if(calendarEventTypes.filter((item: any) => item.type === data.type).length !== 0) {
      const itemData = calendarEventTypes.find((item: any) => item.type === data.type);
      return itemData.image;
    } else {
      return "/resources/icons/question-mark-circle.svg";
    }
  }, [calendarEventTypes, data.type]);

  const getIsDayOverlaping = () => {
    if(eventID === 0 || !overlaping) {
      return 'none';
    } else {
      if(moment(data.dateTimeFrom).isBefore(moment(date), 'day') && moment(data.dateTimeTo).isAfter(moment(date),'day')) {
        return 'full';
      } else if(!moment(data.dateTimeFrom).isBefore(moment(date), 'day') && moment(data.dateTimeTo).isAfter(moment(date),'day')) {
        return 'left';
      } else if(moment(data.dateTimeFrom).isBefore(moment(date), 'day') && !moment(data.dateTimeTo).isAfter(moment(date),'day')) {
        return 'right';
      } else {
        return 'none';
      }
    }
  };

  const getTypeData = useCallback((type: any) => {
    return calendarEventTypes.filter((item: any) => item.type === type).length === 0 ? { background: theme.colors.grey[400], backgroundHover: theme.colors.grey[500], color: theme.colors.black } : calendarEventTypes.find((item: any) => item.type === type);
  }, [calendarEventTypes]);

  const classes = useStyles({
    icon: getIconForCSS,
    backgroundColor: getTypeData(data.type).background,
    backgroundColorHover: getTypeData(data.type).backgroundHover,
    textColor: getTypeData(data.type).color,
    overlap: getIsDayOverlaping(), 
  });

  const getDayInfo = useMemo(() => {
    if(moment(data.dateTimeFrom).isSame(moment(data.dateTimeTo), "date")) {
      if(moment(data.dateTimeFrom).isSame(moment(data.dateTimeTo))) {
        return (
          <span className={classes.eventDay}>
            {t(weekdays[moment(data.dateTimeFrom).isoWeekday() - 1].toLowerCase())}
            ,&nbsp;
            {DateFormat(moment(data.dateTimeFrom),"daymonthname", languageData, t)}
          </span>
        );
      } else {
        return (
          <>
            <span className={classes.eventDay}>
              {t(weekdays[moment(data.dateTimeFrom).isoWeekday() - 1].toLowerCase())}
              ,&nbsp;
              {DateFormat(moment(data.dateTimeFrom),"daymonthname", languageData, t)}
            </span>
            <span className={classes.eventDelimiter}>
              &bull;
            </span>
            <span className={classes.eventTime}>
              {moment(data.dateTimeFrom).format("HH:mm")}
              –
              {moment(data.dateTimeTo).format("HH:mm")}
            </span>
          </>
        );
      }
    } else {
      if(moment(data.dateTimeFrom, 'YYYY-MM-DD HH:mm:ss').isValid() && moment(data.dateTimeTo, 'YYYY-MM-DD HH:mm:ss').isValid()) {
        if(moment(data.dateTimeFrom).format("HH:mm:ss") === "00:00:00" && moment(data.dateTimeTo).format("HH:mm:ss") === "00:00:00") {
          return (
            <span className={classes.eventDay}>
                {DateFormat(moment(data.dateTimeFrom),"daymonthname", languageData, t)}
                &nbsp;
                –
                &nbsp;
                {DateFormat(moment(data.dateTimeTo),"daymonthname", languageData, t)}
            </span>
          );
        } else {
          return (
            <>
              <span className={classes.eventDayTime}>
                {DateFormat(moment(data.dateTimeFrom),"daymonthname", languageData, t)}
                ,&nbsp;
                {moment(data.dateTimeFrom).format("HH:mm")}
                &nbsp;
                –
                &nbsp;
                {DateFormat(moment(data.dateTimeTo),"daymonthname", languageData, t)}
                ,&nbsp;
                {moment(data.dateTimeTo).format("HH:mm")}
              </span>
            </>
          );
        }
      } else {
        return (
          <span className={classes.eventDay}>
            {DateFormat(moment(data.dateTimeFrom),"day", languageData, t)}
            –
            {DateFormat(moment(data.dateTimeFrom),"daymonthname", languageData, t)}
          </span>
        );
      }
    }
  }, [data, classes, t, languageData], [data]);

  const getUsers = useMemo(() => {
    if(Array.isArray(data.userID)) {
      return data.userID.filter((item: any) => getUserData(item).length !== 0);
    } else {
      return [];
    }
  }, [data, getUserData], [data]);

  const getEmployees = useMemo(() => {
    if(Array.isArray(data.employeeID)) {
      return data.employeeID.filter((item: any) => getEmployeeData(item).length !== 0);
    } else {
      return [];
    }
  }, [data, getEmployeeData], [data]);

  const handleChildDetail = (childID: any) => {
    const settings = {
      isOpen: true,
      childID: childID,
    };
    dispatch(setChildCardModal(settings));
  };

  const handleEmployeeDetail = (employeeID: any) => {
    const settings = {
      isOpen: true,
      employeeID: employeeID,
    };
    dispatch(setEmployeeModal(settings));
  };

  const handlePost = (e: any) => {
    e.stopPropagation();
    const settings = {
      isOpen: true,
      postID: data.postID,
    };
    dispatch(setPostDetailModal(settings)); 
  };

  const handleEdit = () => {
    const settings = {
      isOpen: true,
      date: null,
      dateTime: null,
      eventID: eventID,
      recurringEventID: recurringEventID,
    };
    dispatch(setCalendarEventHandleModal(settings));
    handleClose();
  };

  const handleDelete = () => {
    if(recurringEventID) {
      if(recurringEventsCount > 1) {
        handleDeleteRecurring();
      } else {
        handleDeleteSingle();
      }
    } else {
      handleDeleteSingle();
    }
  };

  const handleDeleteRecurring = () => {
    const onAccept = (_: any, option: any, onClose: any) => {
      if(option === "single") {
        calendarService && calendarService.deleteEvent(eventID).then((result: any) => {
          if(result) {
            if(result.status === 204) {
              createNotification(t("calendar_event_deleted"), "success");
              dispatch(deleteCalendarEvent(eventID));
              onClose();
              handleClose();
            } else {
              createNotification(t("calendar_event_not_deleted"), "error");
              onClose();
            }
          } else {
            createNotification(t("calendar_event_not_deleted"), "error");
            onClose();
          }
        }).catch((e: any) => {
          createNotification(!isKey(e.response.data.message) ? e.response.data.message : t("calendar_event_not_deleted"), "error");
          onClose();
        });
      } else if(option === "single_next") {
        const payload = {
          sinceWhenApplyDt: moment(data.dateTimeFrom).subtract(1, "second").format("YYYY-MM-DD HH:mm:ss"),
          deleted: true,
        };
        calendarService && calendarService.editEventRecurring(recurringEventID, payload).then((result: any) => {
          if(result) {
            if(result.status === 200) {
              createNotification(t("calendar_event_recurring_deleted"), "success");
              const settings = {
                recurringEventID: recurringEventID,
                since: moment(data.dateTimeFrom).subtract(1, "second").format("YYYY-MM-DD HH:mm:ss"),
              };
              dispatch(deleteCalendarRecurringEventsSince(settings));
              onClose();
              handleClose();
            } else {
              createNotification(t("calendar_event_recurring_not_deleted"), "error");
              onClose();
            }
          } else {
            createNotification(t("calendar_event_recurring_not_deleted"), "error");
            onClose();
          }
        }).catch((e: any) => {
          createNotification(!isKey(e.response.data.message) ? e.response.data.message : t("calendar_event_recurring_not_deleted"), "error");
          onClose();
        });
      } else if(option === "all") {
        calendarService && calendarService.deleteEventRecurring(recurringEventID).then((result: any) => {
          if(result) {
            if(result.status === 204) {
              createNotification(t("calendar_event_recurring_deleted"), "success");
              dispatch(deleteCalendarRecurringEvents(recurringEventID));
              onClose();
              handleClose();
            } else {
              createNotification(t("calendar_event_recurring_not_deleted"), "error");
              onClose();
            }
          } else {
            createNotification(t("calendar_event_recurring_not_deleted"), "error");
            onClose();
          }
        }).catch((e: any) => {
          createNotification(!isKey(e.response.data.message) ? e.response.data.message : t("calendar_event_recurring_not_deleted"), "error");
          onClose();
        });
      }
    };
    const settings = {
      isOpen: true,
      title: t('calendar_event_delete_recurring'),
      options: [
        {
          name: t('calendar_event_single'),
          value: 'single',
          dataCy: 'singleEventRadio',
        },
        {
          name: t('calendar_event_single_next'),
          value: 'single_next',
          dataCy: 'singleNextEventRadio',
        },
        {
          name: t('calendar_event_all'),
          value: 'all',
          dataCy: 'allEventsRadio',
        },
      ],
      autoClose: false,
      onAccept: onAccept,
      onDecline: null,
    };
    dispatch(setConfirmOptionsModal(settings));
  };

  const handleDeleteSingle = () => {
    const onAccept = (_: any, onClose: any) => {
      calendarService && calendarService.deleteEvent(eventID).then((result: any) => {
        if(result) {
          if(result.status === 204) {
            createNotification(t("calendar_event_deleted"), "success");
            dispatch(deleteCalendarEvent(eventID));
            onClose();
            handleClose();
          } else {
            createNotification(t("calendar_event_not_deleted"), "error");
            onClose();
          }
        } else {
          createNotification(t("calendar_event_not_deleted"), "error");
          onClose();
        }
      }).catch((e: any) => {
        createNotification(!isKey(e.response.data.message) ? e.response.data.message : t("calendar_event_not_deleted"), "error");
        onClose();
      });
    };
    const settings = {
      isOpen: true,
      title: t('calendar_event_delete'),
      content: t('calendar_event_delete_confirm'),
      autoClose: false,
      onAccept: onAccept,
      onDecline: null,
    };
    dispatch(setConfirmModal(settings));
  };

  const handleHoverSet = () => {
    if(eventID !== 0) {
     dispatch(setCalendarHoverEvent(data.eventID));
    }
  };

  const handleHoverUnset = () => {
    if(eventID !== 0) {
      dispatch(setCalendarHoverEvent(null));
    }
  };

  const getIsFullDay = () => {
    if(moment(data.dateTimeFrom).isSame(moment(data.dateTimeTo), "date")) {
      if(moment(data.dateTimeFrom).isSame(moment(data.dateTimeTo))) {
        return true;
      } else {
        return false;
      }
    } else {
      if(moment(data.dateTimeFrom).format("HH:mm:ss") === "00:00:00" && moment(data.dateTimeTo).format("HH:mm:ss") === "00:00:00") {
        return true;
      } else {
        return false;
      }
    }
  };

  const handleShareEvent = (type: any) => {
    const schoolTimezone = getSchoolSettings(data.schoolID, 'timeZone', schoolSettings) === null ? config.APP_TIMEZONE : getSchoolSettings(data.schoolID, 'timeZone', schoolSettings);
    const guests = {
      employees: getEmployees.map((item: any) => { return getEmployeeData(item.employeeID).displayName; }),
      children: allChildren.map((item: any) => { return getChildData(item.childID).displayName; }),
      users: getUsers.map((item: any) => { return getUserData(item).displayName; }),
    };
    if(type === "google") {
      const content = {
        text: data.name,
        dates: getIsFullDay() ? `${moment(data.dateTimeFrom).tz(schoolTimezone).format("YYYYMMDD")}/${moment(data.dateTimeTo).isSame(moment(data.dateTimeFrom)) ? moment(data.dateTimeTo).tz(schoolTimezone).format("YYYYMMDD") : moment(data.dateTimeTo).add(1, "day").tz(schoolTimezone).format("YYYYMMDD")}` : `${moment(data.dateTimeFrom).tz("Etc/Greenwich").format("YYYYMMDDTHHmmss[Z]")}/${moment(data.dateTimeTo).tz("Etc/Greenwich").format("YYYYMMDDTHHmmss[Z]")}`,
        details: `${data.description ? `${data.description}%0A%0A` : ""}${t('calendar_guest_list')}:%0A${Object.values(guests).flat().sort().join(', ')}`,
        ctz: schoolTimezone,
        sprop: `${getAppEnvironmentData().url}calendar`,
        sf: true,
      };
      const url = `https://calendar.google.com/calendar/render?action=TEMPLATE&${convertToKeyValueString(content)}`;
      window.open(url);
    } else if(type === "outlook_personal") {
      const content = {
        allday: getIsFullDay(),
        subject: data.name,
        startdt: getIsFullDay() ? moment(data.dateTimeFrom).tz(schoolTimezone).format("YYYY-MM-DDT00:00:00") : moment(data.dateTimeFrom).tz(schoolTimezone).format("YYYY-MM-DDTHH:mm:ss"),
        enddt: getIsFullDay() ? moment(data.dateTimeTo).isSame(moment(data.dateTimeFrom)) ? "" : moment(data.dateTimeTo).add(1, "day").tz(schoolTimezone).format("YYYY-MM-DDT00:00:00") : moment(data.dateTimeTo).tz(schoolTimezone).format("YYYY-MM-DDTHH:mm:ss"),
        body: `${data.description ? `${data.description}%0A%0A` : ""}${t('calendar_guest_list')}:%0A${Object.values(guests).flat().sort().join(', ')}`,
        sprop: `${getAppEnvironmentData().url}calendar`,
        sf: true,
      };
      const url = `https://outlook.live.com/calendar/deeplink/compose?path=%2Fcalendar%2Faction%2Fcompose&rru=addevent&${convertToKeyValueString(content)}`;
      window.open(url);
    } else if(type === "outlook_business") {
      const content = {
        allday: getIsFullDay(),
        subject: data.name,
        startdt: getIsFullDay() ? moment(data.dateTimeFrom).tz(schoolTimezone).format("YYYY-MM-DDT00:00:00") : moment(data.dateTimeFrom).tz(schoolTimezone).format("YYYY-MM-DDTHH:mm:ss"),
        enddt: getIsFullDay() ? moment(data.dateTimeTo).isSame(moment(data.dateTimeFrom)) ? "" : moment(data.dateTimeTo).add(1, "day").tz(schoolTimezone).format("YYYY-MM-DDT00:00:00") : moment(data.dateTimeTo).tz(schoolTimezone).format("YYYY-MM-DDTHH:mm:ss"),
        body: `${data.description ? `${data.description}%0A%0A` : ""}${t('calendar_guest_list')}:%0A${Object.values(guests).flat().sort().join(', ')}`,
        sprop: `${getAppEnvironmentData().url}calendar`,
        sf: true,
      };
      const url = `https://outlook.office.com/calendar/deeplink/compose?path=%2Fcalendar%2Faction%2Fcompose&rru=addevent&${convertToKeyValueString(content)}`;
      window.open(url);
    }
  };

  const handleCreateIcsFile = () => {
    const UID = `TWIGSEE.${data.eventID}.${moment().unix()}.${userData.userObject.email}`;
    const schoolTimezone = getSchoolSettings(data.schoolID, 'timeZone', schoolSettings) === null ? config.APP_TIMEZONE : getSchoolSettings(data.schoolID, 'timeZone', schoolSettings);
    const guests = {
      employees: getEmployees.map((item: any) => { return getEmployeeData(item.employeeID).displayName; }),
      children: allChildren.map((item: any) => { return getChildData(item.childID).displayName; }),
      users: getUsers.map((item: any) => { return getUserData(item).displayName; }),
    };
    let text = [];
    text.push("BEGIN:VCALENDAR");
    text.push("VERSION:2.0");
    text.push(`PRODID:${config.APP_NAME}`);
    text.push("BEGIN:VEVENT");
    text.push(`UID:${UID}`);
    text.push(`DTSTAMP:${moment().tz(schoolTimezone).format("YYYYMMDDTHHmmss[Z]")}`);
    text.push(`SUMMARY:${data.name}`);
    text.push(`DESCRIPTION:${data.description ? `${data.description}\\n\\n` : ""}${t('calendar_guest_list')}:\\n${Object.values(guests).flat().sort().join(', ')}`);
    text.push("CLASS:PUBLIC");
    text.push(`DTSTART${getIsFullDay() ? `;VALUE=DATE:${moment(data.dateTimeFrom).tz(schoolTimezone).format("YYYYMMDD")}` : `:${moment(data.dateTimeFrom).tz(schoolTimezone).format("YYYYMMDDTHHmmss[Z]")}`}`);
    text.push(`DTEND${getIsFullDay() ? `;VALUE=DATE:${moment(data.dateTimeFrom).add(1, "day").tz(schoolTimezone).format("YYYYMMDD")}` : `:${moment(data.dateTimeTo).tz(schoolTimezone).format("YYYYMMDDTHHmmss[Z]")}`}`);
    text.push(`END:VEVENT`);
    text.push(`END:VCALENDAR`);
    let blob = new Blob([text.join("\n")], {type: "text/calendar;charset=utf-8"});
    let file_name = "TWIGSEE_ICS_" + moment().format("YYYY_MM_DD_HH_mm_ss") + ".ics";
    saveAs(blob, file_name);  
  };

  const handleMetaData = () => {
    const parentType = data.metaData.parentType;
    const parentID = data.metaData.parentID;
    if(parentType === "absence") {
      handleOpenExcuseNote(parentID);
    } else if(parentType === "substitution") {
      handleOpenSubstitution(parentID);
    }
  };

  const handleOpenExcuseNote = (absenceID: any) => {
    const settings = {
      isOpen: true,
      absenceID: parseInt(absenceID),
      childID: data.childID,
      type: "single",
      readOnly: true,
    };
    dispatch(setExcuseNotesDetailModal(settings));
  };

  const handleOpenSubstitution = (substitutionID: any) => {
    const settings = {
      isOpen: true,
      substitutionID: parseInt(substitutionID),
      childID: data.childID,
      readOnly: true,
    };
    dispatch(setSubstitutionsDetailModal(settings));
  };

  return (
    <div className={classes.eventWrapper}>
      <div className={`${classes.event} ${hoverEvent === data.eventID ? 'hover' : null}`} onClick={handleClick} onMouseEnter={handleHoverSet} onMouseLeave={handleHoverUnset}>
        {getIcon} 
        <Clamp withTooltip lines={1}>
          <span className={classes.name}>{data.name}</span>
        </Clamp> 
      </div> 
      {
        state.isVisible ? (
          <Popover className={classes.popover} id={eventPopover} open={Boolean(state.anchorEl)} anchorEl={state.anchorEl} onClose={handleClose} anchorOrigin={{ vertical: 'center', horizontal: 'center' }} transformOrigin={{ vertical: 'center', horizontal: 'center' }}>
            <div className={classes.popoverHeader}>
              <div className={classes.popoverTopButtons}>
                {
                  data.isEditable ? (
                    <>
                      <IconButton className={classes.popoverButton} iconColor={theme.colors.white} tooltip={t('calendar_event_edit')} tooltipMaxWidth={400} size="small" onClick={handleEdit}>
                        <SVG src="edit"/>
                      </IconButton>
                      <IconButton className={classes.popoverButton} iconColor={theme.colors.white} tooltip={t('calendar_event_delete')} tooltipMaxWidth={400} size="small" onClick={handleDelete}>
                        <SVG src="trash"/>
                      </IconButton>
                    </>
                  ) : null
                }
                {
                  (data.postID && data.postID !== 0 && data.postID !== "") ? (
                    <IconButton className={classes.popoverButton} iconColor={theme.colors.white} tooltip={t('post')} tooltipMaxWidth={400} size="small" onClick={handlePost}>
                      <SVG src="posts"/>
                    </IconButton>
                  ) : null
                }
                <IconButton className={classes.popoverButton} iconColor={theme.colors.white} tooltip={t('close')} tooltipMaxWidth={400} size="small" onClick={handleClose}>
                  <SVG src="close"/>
                </IconButton>
              </div>
              {
                isShareable ? (
                  <div className={classes.popoverBottomButtons}>
                    <IconButton className={classes.popoverButton} iconColor={theme.colors.white} tooltip={t('calendar_add_event_to_google_calendar')} tooltipMaxWidth={400} size="small" onClick={() => handleShareEvent("google")}>
                      <SVG src="google-calendar"/>
                    </IconButton>
                    <IconButton className={classes.popoverButton} iconColor={theme.colors.white} tooltip={t('calendar_add_event_to_outlook_calendar')} tooltipMaxWidth={400} size="small" onClick={() => handleShareEvent("outlook_personal")}>
                      <SVG src="outlook"/>
                    </IconButton>
                    <IconButton className={classes.popoverButton} iconColor={theme.colors.white} tooltip={t('calendar_add_event_to_office-365_calendar')} tooltipMaxWidth={400} size="small" onClick={() => handleShareEvent("outlook_business")}>
                      <SVG src="office-365"/>
                    </IconButton>
                    <IconButton className={classes.popoverButton} iconColor={theme.colors.white} tooltip={t('calendar_event_export_as_ics')} tooltipMaxWidth={400} size="small" onClick={handleCreateIcsFile}>
                      <SVG src="download"/>
                    </IconButton>
                  </div>  
                ) : null
              }
            </div>
            <div className={classes.popoverDetails}>
              <div className={classes.popoverDetail}>
                <div className={classes.popoverDetailLeft}>
                  <span className={classes.popoverEventIcon}/>
                </div>
                <div className={classes.popoverDetailRight}>
                  <span className={classes.eventName}>
                    {data.name}
                  </span>
                  <div className={classes.eventDates}>{getDayInfo}</div>
                </div>
              </div>
              <div className={classes.popoverDetail}>
                <div className={classes.popoverDetailLeft}>
                  <Tooltip title={t('calendar_event_type')} maxWidth={400}>
                    <SVG src="calendar"/>
                  </Tooltip>
                </div>
                <div className={classes.popoverDetailRight}>
                  {
                    data.metaData ? (
                      <>
                        {
                          data.metaData.parentType === "absence" ? (
                            <span className={classes.tag} onClick={handleMetaData}>{t('excuse_note')}</span>
                          ) : null
                        }
                        {
                          data.metaData.parentType === "substitution" ? (
                            <span className={classes.tag} onClick={handleMetaData}>{t('substitution')}</span>
                          ) : null
                        }
                      </>
                    ) : (
                      <>{(data.type === "event" && data.recurringEventID && recurringEventsCount > 1) ? t('calendar_event_recurring') : t(convertToSnakeCase(data.type))}</>
                    )
                  }
                </div>
              </div>
              {
                ((eventOnlyClasses.length === 1 && getClassData(eventOnlyClasses[0].classID).length !== 0) || eventOnlyClasses.length > 1) ? (
                  <div className={classes.popoverDetail}>
                    <div className={`${classes.popoverDetailLeft} iconTop`}>
                      <Tooltip title={t('children')} maxWidth={400}>
                        <SVG src="people"/>
                      </Tooltip>
                    </div>
                    <div className={classes.popoverDetailRight}>
                      {
                        eventOnlyClasses.length > 1 ? (
                          <>
                            <span className={classes.count}>{eventOnlyClasses.length} {(eventOnlyClasses.length > 1 && eventOnlyClasses.length < 5) ? t('classes').toLowerCase() : t('classes_more').toLowerCase()}</span>
                            <div className={classes.classList}>
                              {
                                eventOnlyClasses.map((item: any, key: any) => {
                                  return Object.keys(getClassData(item.classID)).length > 0 ? (
                                    <div className={classes.theClassWrapper} key={`k_${key}`}>
                                      <span className={classes.theClass}>
                                        {htmlParse(getClassData(item.classID).name)}
                                      </span>
                                      <div className={classes.guestList}>
                                        {
                                          allChildren.filter((subItem: any) => subItem.classID === item.classID).map((subItem: any, key: any) => getChildData(subItem.childID).length !== 0 ? (
                                            <span className={classes.guest} onClick={() => handleChildDetail(subItem.childID)} key={`k_${key}`}>
                                              <AuthenticatedImage className={classes.guestUserPhoto} thumbLink={getChildData(subItem.childID).photo.thumbLink}/>
                                              {getChildData(subItem.childID).displayName}
                                            </span>
                                          ) : null)
                                        }
                                      </div>
                                    </div>
                                  ) : null;
                                })
                              }
                            </div>
                          </>
                        ) : (
                          <>
                            {
                              Object.keys(getClassData(eventOnlyClasses[0].classID)).length > 0 ? (
                                <>
                                  <span className={classes.theClass}>
                                    {htmlParse(getClassData(eventOnlyClasses[0].classID).name)}
                                  </span>
                                  <div className={classes.guestList}>
                                    {
                                      allChildren.filter((item: any) => item.classID === eventOnlyClasses[0].classID).map((item: any, key: any) => getChildData(item.childID).length !== 0 ? (
                                        <span className={classes.guest} onClick={() => handleChildDetail(item.childID)} key={`k_${key}`}>
                                          <AuthenticatedImage className={classes.guestChildPhoto} thumbLink={getChildData(item.childID).photo.thumbLink}/>
                                          {getChildData(item.childID).displayName}
                                        </span>
                                      ) : null)
                                    }
                                  </div>
                                </>
                              ) : null
                            }
                          </>
                        )
                      }
                    </div>
                  </div>
                ) : null
              }
              {
                ((eventChildren.length === 1 && getChildData(eventChildren[0].childID).length !== 0) || eventChildren.length > 1) ? (
                  <div className={classes.popoverDetail}>
                    <div className={`${classes.popoverDetailLeft} iconTop`}>
                      <Tooltip title={t('children')} maxWidth={400}>
                        <SVG src="people"/>
                      </Tooltip>
                    </div>
                    <div className={classes.popoverDetailRight}>
                      {
                        eventChildren.length > 1 ? (
                          <>
                            <span className={classes.count}>{eventChildren.length} {(eventChildren.length > 1 && eventChildren.length < 5) ? t('children').toLowerCase() : t('children_more').toLowerCase()}</span>
                            <div className={classes.guestList}>
                              {
                                eventChildren.map((item: any, key: any) => getChildData(item.childID).length !== 0 ? (
                                  <span className={classes.guest} onClick={() => handleChildDetail(item.childID)} key={`k_${key}`}>
                                    <AuthenticatedImage className={classes.guestChildPhoto} thumbLink={getChildData(item.childID).photo.thumbLink}/>
                                    {getChildData(item.childID).displayName}
                                  </span>
                                ) : null)
                              }
                            </div>
                          </>
                        ) : (
                          <span className={classes.guest} onClick={() => handleChildDetail(eventChildren[0].childID)}>
                            <AuthenticatedImage className={classes.guestChildPhoto} thumbLink={getChildData(eventChildren[0].childID).photo.thumbLink}/>
                            {getChildData(eventChildren[0].childID).displayName}
                          </span>
                        )
                      }
                    </div>
                  </div>
                ) : null
              }
              {
                ((getUsers.length === 1 && getUserData(getUsers[0]).length !== 0) || getUsers.length > 1) ? (
                  <div className={classes.popoverDetail}>
                    <div className={`${classes.popoverDetailLeft} iconTop`}>
                      <Tooltip title={t('employees')} maxWidth={400}>
                        <SVG src="employees"/>
                      </Tooltip>
                    </div>
                    <div className={classes.popoverDetailRight}>
                      {
                        getUsers.length > 1 ? (
                          <>
                            <span className={classes.count}>{getUsers.length} {(getUsers.length > 1 && getUsers.length < 5) ? t('teachers').toLowerCase() : t('teachers_more').toLowerCase()}</span>
                            <div className={classes.guestList}>
                              {
                                getUsers.map((item: any, key: any) => getUserData(item).length !== 0 ? (
                                  <span className={`${classes.guest} noLink`} key={`k_${key}`}>
                                    <AuthenticatedImage className={classes.guestUserPhoto} thumbLink={getUserData(item).photo.thumbLink}/>
                                    {getUserData(item).firstname} {getUserData(item).surname}
                                  </span>
                                ) : null)
                              }
                            </div>
                          </>
                        ) : (
                          <span className={`${classes.guest} noLink`}>
                            <AuthenticatedImage className={classes.guestUserPhoto} thumbLink={getUserData(getUsers[0]).photo.thumbLink}/>
                            {getUserData(getUsers[0]).firstname} {getUserData(getUsers[0]).surname}
                          </span>
                        )
                      }
                    </div>
                  </div>
                ) : null
              }
              {
                ((getEmployees.length === 1 && getEmployeeData(getEmployees[0]).length !== 0) || getEmployees.length > 1) ? (
                  <div className={classes.popoverDetail}>
                    <div className={`${classes.popoverDetailLeft} iconTop`}>
                      <Tooltip title={t('employees')} maxWidth={400}>
                        <SVG src="employees"/>
                      </Tooltip>
                    </div>
                    <div className={classes.popoverDetailRight}>
                      {
                        getEmployees.length > 1 ? (
                          <>
                            <span className={classes.count}>{getEmployees.length} {(getEmployees.length > 1 && getEmployees.length < 5) ? t('employees').toLowerCase() : t('employees_more').toLowerCase()}</span>
                            <div className={classes.guestList}>
                              {
                                getEmployees.map((item: any, key: any) => getEmployeeData(item).length !== 0 ? (
                                  <span className={classes.guest} onClick={() => handleEmployeeDetail(item)} key={`k_${key}`}>
                                    <AuthenticatedImage className={classes.guestUserPhoto} thumbLink={getEmployeeData(item).photo.thumbLink}/>
                                    {getEmployeeData(item).firstname} {getEmployeeData(item).surname}
                                  </span>
                                ) : null)
                              }
                            </div>
                          </>
                        ) : (
                          <span className={classes.guest} onClick={() => handleEmployeeDetail(getEmployees[0])}>
                            <AuthenticatedImage className={classes.guestUserPhoto} thumbLink={getEmployeeData(getEmployees[0]).photo.thumbLink}/>
                            {getEmployeeData(getEmployees[0]).firstname} {getEmployeeData(getEmployees[0]).surname}
                          </span>
                        )
                      }
                    </div>
                  </div>
                ) : null
              }
              {
                data.description ? (
                  <div className={classes.popoverDetail}>
                    <div className={classes.popoverDetailLeft}>
                      <Tooltip title={t('calendar_event_description')} maxWidth={400}>
                        <SVG src="info-circle-outlined"/>
                      </Tooltip>
                    </div>
                    <div className={classes.popoverDetailRight}>
                      <Clamp
                        lines={4}
                        maxLines={100}
                        withToggle
                        showMoreElement={({ toggle }: any) => (
                          <div className={classes.showToggleWrapper}>
                            <span className={classes.showToggle} onClick={toggle}>
                              {t('show_more')}
                            </span>
                          </div>
                        )}
                        showLessElement={({ toggle }: any) => (
                          <div className={classes.showToggleWrapper}>
                            <span className={classes.showToggle} onClick={toggle}>
                              {t('show_less')}
                            </span>
                          </div>
                        )}
                      >
                        <span className={classes.description}>
                          {htmlParse(data.description)}
                        </span>
                      </Clamp> 
                    </div>
                  </div> 
                ) : null
              }
              {
                data.media && data.media.length !== 0 ? (
                  <div className={classes.popoverDetail}>
                    <div className={classes.popoverDetailLeft}>
                      <Tooltip title={t('calendar_event_attachments')} maxWidth={400}>
                        <SVG src="attachment"/>
                      </Tooltip>
                    </div>
                    <div className={classes.popoverDetailRight}>
                      <CalendarGallery eventID={data.eventID}/>
                    </div>
                  </div>
                ) : null
              }
            </div>
          </Popover>
        ) : null
      }
    </div>
  );
}

export default CalendarEventItem;